import React, { useEffect, useState } from 'react';
import { useCallback } from 'react';
import { authHeader } from '../../helpers/auth-header';
import SpinnerOverlay from '../layout/SpinnerOverlay';
import DOMPurify from "dompurify";

const JsonToHtml = (props) => {
    let { url, json, jsonPropertyPath, componentID } = props;

    const [data, setData] = useState(null);

    const fetchUrlData = useCallback(async () => {
        try {
            const response = await fetch(`./Data/GetJsonByUrl?url=${encodeURIComponent(url)}`, {
                method: 'GET',
                headers: authHeader(false, true, 'text/plain; charset=utf-8'),
            });
            const jsonData = await response.json();

            // Incase there is a sub-level of the JSON object we need to get, ie {sell2uk: [{...}]} or if we get the root
            const extractedData = jsonPropertyPath
                ? jsonPropertyPath.split('.').reduce((obj, key) => (obj ? obj[key] : null), jsonData)
                : jsonData;

            setData(extractedData); // Assuming the API response has this structure

        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [jsonPropertyPath, url]);

    useEffect(() => {
        if (url || json) {
            if (url && url !== '') {
                fetchUrlData();
            } else {
                setData(json)
            }
        }
    }, [fetchUrlData, json, url]);

    const parseTextWithLink = (text) => {
        if (typeof text !== 'string') return text;

        // Check if it contains HTML tags
        const containsHTML = /<\/?[a-z][\s\S]*>/i.test(text);

        if (containsHTML) {
            // If you trust the HTML, you can directly inject it
            return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />;
        }

        // Split the text into normal text and anchor link parts match [text](url)
        const textParts = text.split(/(\[[^\]]+\]\([^)]+\))/g);

        // Loop through all the different text parts
        return textParts.map((part, index) => {
            // Check if each part matches the regex
            const match = part.match(/\[([^\]]+)\]\(([^)]+)\)/);

            if (match) {
                // Split the matched text part into an array
                const displayText = match[1];
                const url = match[2];

                // Generate and return the Anchor Link
                return (
                    <a key={index} className="u-no-wrap c-text-link" style={{display: "inline-flex", fontSize: "inherit"}} target="_blank" rel="noreferrer" href={url}>
                        {displayText}
                    </a>
                );
            }

            // ... else return the the text
            return <React.Fragment key={index}>{part}</React.Fragment>;
        });
    };

    const renderElement = (element) => {
        switch (element.element) {
            case "h1":
                return <h1 className='c-broken-underline u-component-spacing'>{parseTextWithLink(element.text === undefined ? element.html : element.text)}</h1>;

            case "h2":
                return <h2 className='u-component-spacing--2x'>{parseTextWithLink(element.text === undefined ? element.html : element.text)}</h2>;

            case "h3":
                return <h3 className='u-component-spacing--2x'>{parseTextWithLink(element.text === undefined ? element.html : element.text)}</h3>;

            case "h4":
                return <h4 className='u-component-spacing--2x'>{parseTextWithLink(element.text === undefined ? element.html : element.text)}</h4>;

            case "p":
                return <p className='u-component-spacing' style={{ marginLeft: "3rem" }}>{parseTextWithLink(element.text === undefined  ? element.html : element.text)}</p>;

            case "small":
                return <small className='u-component-spacing'>{parseTextWithLink(element.text === undefined  ? element.html : element.text)}</small>;

            case "table":
                return (
                    <table className={`table u-component-spacing--2x ${element.class}`}>
                        <tbody>
                            {element.rows.map((row, i) => (
                                <tr key={i} className={i % 2 === 0 ? "odd" : "even"}>
                                    {row.cells.map((cell, cellIndex) => {
                                        const CellTag = cell.element;
                                        return <CellTag key={cellIndex}>{parseTextWithLink(cell.text === undefined ? cell.html : cell.text)}</CellTag>;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );

            case "ul":
            case "ol":
                return (
                    <ul>
                        {element.items.map((item, i) => (
                            <li key={i} className='u-component-spacing' style={{lineHeight:"1.8em"} }>
                                {parseTextWithLink(item.text === undefined  ? item.html : item.text)}
                            </li>
                        ))}
                    </ul>
                );

            default:
                return <div className='' >{parseTextWithLink(element.text === undefined  ? element.html : element.text)}</div>;
        }
    };

    if (data) {
        return (
            <div id={componentID}>
                {data.map((item, index) => (
                    <React.Fragment key={index}>
                        {renderElement(item)}</React.Fragment>))
                    }
            </div>
        );
    } else {
        return (
            <SpinnerOverlay />
        );
    }
};

export default JsonToHtml;